import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { CheckCircle2, XCircle, Loader2, ArrowRight, Home } from "lucide-react";

export default function MembershipResult({ outcome }) {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const appId = params.get("application_id");
  const [state, setState] = useState({ loading: outcome === "success", status: null, data: null });
  const attempts = useRef(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (outcome !== "success" || !sessionId) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/memberships/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") { setState({ loading: false, status: "paid", data }); return; }
        if (data.status === "expired" || data.payment_status === "expired") { setState({ loading: false, status: "expired", data }); return; }
        attempts.current += 1;
        if (attempts.current >= 6) { setState({ loading: false, status: "timeout", data }); return; }
        setTimeout(poll, 2000);
      } catch {
        setState({ loading: false, status: "error", data: null });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [outcome, sessionId]);

  if (outcome === "cancel") {
    return (
      <ResultShell icon={XCircle} iconColor="#C8795A" title="Pagamento annullato"
        msg="Hai annullato il pagamento. La tua richiesta è stata salvata — puoi riprovare quando vuoi.">
        <Link to="/adesione" className="inline-flex items-center gap-2 rounded-full bg-[#4A6B53] text-white px-6 py-3 text-sm font-bold hover:bg-[#3D5844]">
          Riprova <ArrowRight size={16}/>
        </Link>
        <Link to="/" className="inline-flex items-center gap-2 rounded-full border-2 border-[#E5DCD0] px-6 py-3 text-sm font-bold">
          <Home size={16}/> Torna alla home
        </Link>
      </ResultShell>
    );
  }

  if (state.loading) {
    return (
      <ResultShell icon={Loader2} iconColor="#4A6B53" spin title="Stiamo confermando il pagamento…"
        msg="Aspetta qualche istante, stiamo verificando con Stripe." />
    );
  }

  if (state.status === "paid") {
    return (
      <ResultShell icon={CheckCircle2} iconColor="#4A6B53" title="Benvenuto nel Network!"
        msg={`Pagamento ricevuto (€${state.data?.amount}). Ti invieremo a breve tutte le informazioni operative per partecipare ai gruppi di lavoro e ricevere il kit associativo.`}>
        <Link to="/fattorie" className="inline-flex items-center gap-2 rounded-full bg-[#C8795A] text-white px-6 py-3 text-sm font-bold hover:bg-[#B3684B]">
          Scopri le fattorie del Network <ArrowRight size={16}/>
        </Link>
        <Link to="/" className="inline-flex items-center gap-2 rounded-full border-2 border-[#E5DCD0] px-6 py-3 text-sm font-bold">
          <Home size={16}/> Torna alla home
        </Link>
      </ResultShell>
    );
  }

  return (
    <ResultShell icon={XCircle} iconColor="#C8795A" title="Pagamento non completato"
      msg="Il pagamento non risulta ancora completato. Se pensi sia un errore scrivici a info@agrigiochiamo.it citando il tuo nome.">
      <Link to="/adesione" className="inline-flex items-center gap-2 rounded-full bg-[#4A6B53] text-white px-6 py-3 text-sm font-bold hover:bg-[#3D5844]">
        Riprova
      </Link>
      <Link to="/" className="inline-flex items-center gap-2 rounded-full border-2 border-[#E5DCD0] px-6 py-3 text-sm font-bold"><Home size={16}/> Home</Link>
    </ResultShell>
  );
}

function ResultShell({ icon: Icon, iconColor, spin, title, msg, children }) {
  return (
    <div className="min-h-[calc(100vh-80px)] flex items-center justify-center bg-[#FBF9F6] px-6 py-14">
      <div className="w-full max-w-xl rounded-[2rem] bg-white border border-[#E5DCD0] p-8 md:p-14 text-center shadow-sm">
        <div className={`w-16 h-16 rounded-2xl mx-auto mb-6 flex items-center justify-center ${spin ? "" : ""}`} style={{ background: `${iconColor}15`, color: iconColor }}>
          <Icon size={32} className={spin ? "animate-spin" : ""}/>
        </div>
        <h1 className="font-display font-black text-3xl md:text-4xl text-[#2C312E]">{title}</h1>
        <p className="mt-4 text-[#58625B] leading-relaxed">{msg}</p>
        {children && <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">{children}</div>}
      </div>
    </div>
  );
}
